import styled from "styled-components";
import {Popover} from "@mui/material";
import {fontFamilies, radii, shadows} from "theme/tokens";

/**
 * The floating surface the offer opens onto.
 *
 * A MUI `Popover` — which is a `Modal` underneath — rather than a bare absolutely-positioned div,
 * because everything this disclosure needs for the keyboard is already in that primitive and is
 * exactly what a hand-rolled overlay forgets: focus moves into the surface on open, is trapped while
 * it is up, Escape closes it, and focus returns to the trigger afterwards. Nothing here wires an
 * app-owned focus restore, so MUI's own trap is the single owner on every path.
 *
 * `& .MuiPaper-root` rather than `slotProps={{paper: {style}}}`: `StyledEngineProvider injectFirst`
 * (see `theme/AppTheme.tsx`) puts MUI's sheet ahead of styled-components, so these overrides win
 * without an `!important` war — the same pattern as `components/SideSheet.styles.ts`. It also keeps the
 * declarations OUT of the component file, which an inline `style` object would not.
 *
 * `shadows.popover`, not `shadows.modal`: the modal shadow is a 100px drop sized for a surface that
 * takes over the viewport, and under a 28px trigger it reads as a bruise rather than as elevation.
 *
 * **The width is bounded on both sides.** `max-width` keeps it inside the narrowest phone this page
 * supports (320px, less the 16px the viewport wants on each side); the fixed `width` stops the panel
 * from collapsing to the width of its longest unbreakable word, which is what an unconstrained
 * popover does with a code chip and a hostname in it.
 */
export const OfferPopover = styled(Popover)`
	& .MuiPaper-root {
		width: 300px;
		max-width: calc(100vw - 32px);
		border-radius: ${radii.modal};
		background: ${({theme}) => theme.tokens.card};
		background-image: none;
		border: 1px solid ${({theme}) => theme.tokens.cardBorder};
		box-shadow: ${shadows.popover};
		overflow: visible;
	}
`;

/**
 * The offer itself — badge, headline, terms, code and CTA — unchanged from the panel that used to sit
 * in the card, in content AND in treatment.
 *
 * `accentTint` is kept deliberately. It composites over the paper's opaque `card` exactly as it used to
 * composite over the card itself, so the surface a recipient sees is the promotion the owner asked for
 * on 2026-08-18 — this ticket moved WHERE the offer appears, not what it looks like once it does.
 *
 * It is NOT kept because of axe. MEASURED on this branch: removing the tint changed the offer's audit
 * in `e2e/download-acquisition-footer.spec.ts` not at all — a portalled floating surface is what axe
 * cannot composite here, so both the tinted and the untinted panel return the same two
 * background-undeterminable `color-contrast` findings. That audit's discount list names both reasons
 * for exactly this, and the real ratios are recorded there.
 */
export const ReferralOfferPanel = styled.section`
	padding: 14px 16px;
	border-radius: ${radii.modal};
	background: ${({theme}) => theme.tokens.accentTint};
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

/**
 * The offer's eyebrow pill. It names the RELATIONSHIP ("Your invite") since ui#1693 rather than a
 * countdown — the deadline moved into the body, where it reads as permission instead of pressure.
 *
 * Mono + letter-spacing is the catalog's established eyebrow treatment, and it is what makes this read
 * as a promotion at a glance rather than as one more sentence in a footer.
 */
export const OfferBadge = styled.span`
	align-self: flex-start;
	font-family: ${fontFamilies.mono};
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: ${({theme}) => theme.tokens.blueInk};
	background: ${({theme}) => theme.tokens.blue};
	border-radius: ${radii.pill};
	padding: 3px 8px;
`;

/** The offer's one-line promise. `ink`, not `ink2`: this is the line the whole panel exists to say. */
export const OfferHeadline = styled.p`
	margin: 0;
	color: ${({theme}) => theme.tokens.ink};
	font-size: 14px;
	font-weight: 700;
	line-height: 1.3;
`;

/** The terms in full — the deadline, and when the week actually arrives. */
export const OfferBody = styled.p`
	margin: 0;
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 12.5px;
	line-height: 1.45;
`;

/**
 * The code and the CTA on one line, wrapping rather than overflowing.
 *
 * `flex-wrap` is load-bearing at the popover's own width: the code is 8 fixed characters and the CTA
 * carries a hostname, so the two cannot share a row and must stack instead of forcing a horizontal
 * scroll onto a surface that has no business having one.
 */
export const OfferActions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	margin-top: 2px;
`;

/**
 * The applied code and its label, kept together so they wrap as one unit rather than the label ending a
 * line and its code starting the next.
 */
export const OfferCodeGroup = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
`;

/**
 * The VISIBLE label for the code beside it (ui#1223).
 *
 * It replaced an `aria-label` on {@link OfferCode}, which did nothing: `<code>` is a
 * name-from-author-PROHIBITED role, so screen readers ignored the attribute and axe flagged
 * `aria-prohibited-attr` — the code had no accessible name at all. Visible text fixes both audiences at
 * once, because a bare 8-character string was equally unexplained to a sighted reader.
 */
export const OfferCodeLabel = styled.span`
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 12px;
`;

/**
 * The applied code, rendered rather than merely linked.
 *
 * Mono and letter-spaced because this is a string people RETYPE — on the other device they are about to
 * open, or off a screenshot — and the api's alphabet already drops the confusable glyphs on the same
 * reasoning.
 */
export const OfferCode = styled.code`
	font-family: ${fontFamilies.mono};
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.06em;
	color: ${({theme}) => theme.tokens.ink};
	background: ${({theme}) => theme.tokens.chip};
	border: 1px solid ${({theme}) => theme.tokens.line};
	border-radius: 8px;
	padding: 4px 8px;
`;

/**
 * The CTA.
 *
 * A LINK styled as a button, never a `<button>`: it navigates to another origin, so the element has to
 * be the one screen readers announce as a link and the browser lets a recipient open in yet another
 * tab. `min-height: 32px` keeps the target reachable on a phone.
 */
export const OfferCta = styled.a`
	display: inline-flex;
	align-items: center;
	min-height: 32px;
	padding: 0 12px;
	border-radius: ${radii.pill};
	background: ${({theme}) => theme.tokens.blue};
	color: ${({theme}) => theme.tokens.blueInk};
	font-size: 12.5px;
	font-weight: 600;
	text-decoration: none;
	transition: background 0.14s ease;

	&:hover {
		background: ${({theme}) => theme.tokens.blued};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;
